/**
 * useMenu — fetches the rendered menu for the current branch and adapts the
 * catalog-service `MenuRenderResponse` shape into the UI-friendly shape used
 * by `HomeScreen` / `ItemDetailScreen`.
 *
 * Resolution rules:
 *   - menuId comes from the active branch selected by `useBranch()`.
 *   - If the branch context isn't populated yet, falls back to env vars
 *     `VITE_DEFAULT_MENU_ID` / `VITE_DEFAULT_BRANCH_ID`.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { availabilityApi, menuApi } from "@/api/services";
import { useBranch } from "@/store/branch";
import { setMenuCurrency } from "@/hooks/useFormatPrice";
import { branchIdentifier } from "@/lib/fulfillment";
import {
  durationUntilNextMenuBoundary,
  isWithinOperatingHours,
} from "@/lib/menu-dayparting";
import type { Tone } from "@/components/FoodImg";
import type { ModifierGroup } from "@/types/menu";
import type {
  LocalizedString,
  MenuRenderCategoryItemsInner,
  MenuRenderModifierGroup,
  MenuRenderResponse,
  MenuRenderVariantGroup,
  NutritionalInformationObj,
  PricingInfo,
} from "@/api/catalog-service/model";

/**
 * Pull the first non-empty price currency out of a menu response — every
 * `PricingInfo` carries one, so this gives us a reliable default symbol
 * when the branch config doesn't declare a currency at the top level.
 */
function findFirstPriceCurrency(
  resp: MenuRenderResponse | null | undefined,
): string | null {
  if (!resp) return null;
  for (const cat of resp.categories ?? []) {
    for (const it of cat.items ?? []) {
      const c = (it as { price?: PricingInfo | null }).price?.currency;
      if (typeof c === "string" && c.length > 0) return c;
    }
  }
  return null;
}

export type UiMenuItem = {
  id: string;
  name: string;
  desc: string;
  price: number;
  cal: number;
  cat: string;
  categoryName: string;
  categoryNameRaw?: any;
  popular?: boolean;
  badges?: string[];
  tone: Tone;
  imageUrl?: string | null;
  /** Whether this item is currently sellable. Defaults to true if the source
   *  item doesn't carry an `isAvailable` flag. May be flipped to false by a
   *  branch-level block fetched via `availabilityApi.check()`. */
  isAvailable: boolean;
  /** Original API payload, kept around for screens that need richer data
   *  (e.g. ItemDetailScreen reads modifierGroups + nutritionalInformation). */
  raw: MenuRenderCategoryItemsInner;
};

export type UiCategory = {
  id: string;
  label: string;
  items: UiMenuItem[];
};

const TONE_CYCLE: Tone[] = [
  "ochre",
  "rose",
  "amber",
  "tomato",
  "olive",
  "honey",
];

/** Stable hash → tone mapping so the same item gets the same tone every render. */
function pickTone(id: string, fallbackIndex: number): Tone {
  if (!id) return TONE_CYCLE[fallbackIndex % TONE_CYCLE.length];
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return TONE_CYCLE[Math.abs(h) % TONE_CYCLE.length];
}

/** Pick the best string from a `LocalizedString` for the active language. */
export function localize(
  s: LocalizedString | null | undefined,
  lang: string,
): string {
  if (!s) return "";
  const normalizedLang = lang.toLowerCase().split(/[-_]/)[0];
  const direct =
    (s as Record<string, unknown>)[lang] ??
    (s as Record<string, unknown>)[normalizedLang];
  if (typeof direct === "string" && direct) return direct;
  if (typeof s.en === "string" && s.en) return s.en;
  if (typeof s.ar === "string" && s.ar) return s.ar;
  // Fall back to first non-empty string value.
  for (const v of Object.values(s as Record<string, unknown>)) {
    if (typeof v === "string" && v) return v;
  }
  return "";
}

function priceOf(p: PricingInfo | null | undefined): number {
  if (!p) return 0;
  const n = typeof p.amount === "number" ? p.amount : Number(p.amount);
  return Number.isFinite(n) ? n : 0;
}

/** Turn API tags into 0-3 short, human-friendly badge labels. */
function tagsToBadges(tags?: string[] | null): string[] | undefined {
  if (!tags || tags.length === 0) return undefined;
  // Lightly title-case the tag and de-dupe; keep at most 3.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    if (!raw) continue;
    const t = raw.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    // Common synonyms → match the design's existing badge labels so the
    // ItemRow tone-mapping (Spicy / Vegan / Veg / Popular / Chef's pick)
    // continues to work.
    let label = t;
    if (key === "vegan") label = "Vegan";
    else if (key === "vegetarian" || key === "veg") label = "Veg";
    else if (key === "spicy") label = "Spicy";
    else if (key === "popular") label = "Popular";
    else if (key === "chefs-pick" || key === "chef's pick")
      label = "Chef's pick";
    else if (key === "gf" || key === "gluten-free") label = "GF";
    else label = t.charAt(0).toUpperCase() + t.slice(1);
    out.push(label);
    if (out.length >= 3) break;
  }
  return out.length ? out : undefined;
}

function isPopular(item: MenuRenderCategoryItemsInner): boolean {
  const tags = item.tags ?? [];
  return tags.some((t) => {
    const k = t?.toLowerCase?.() ?? "";
    return k === "popular" || k === "chefs-pick" || k === "chef's pick";
  });
}

/** Adapt a single category's item to the UI shape. */
function toUiItem(
  item: MenuRenderCategoryItemsInner,
  categoryId: string,
  categoryNameStr: string,
  categoryNameRaw: any,
  lang: string,
  index: number,
): UiMenuItem {
  const cal = item.nutritionalInformation?.calories ?? 0;
  return {
    id: item.id,
    name: localize(item.name, lang),
    desc: localize(item.description, lang),
    price: priceOf(item.price),
    cal: typeof cal === "number" ? cal : 0,
    cat: categoryId,
    categoryName: categoryNameStr,
    categoryNameRaw,
    popular: isPopular(item),
    badges: tagsToBadges(item.tags),
    tone: pickTone(item.id, index),
    imageUrl: item.imageUrl ?? null,
    isAvailable: (item as { isAvailable?: boolean }).isAvailable !== false,
    raw: item,
  };
}

export function adaptMenuResponse(
  resp: MenuRenderResponse | null,
  lang: string,
  now = new Date(),
): { categories: UiCategory[]; items: UiMenuItem[] } {
  if (!resp || !resp.categories || resp.categories.length === 0) {
    return { categories: [], items: [] };
  }
  let runningIdx = 0;
  const categories: UiCategory[] = [];
  for (const cat of resp.categories) {
    if (!cat) continue;
    if (cat.isAvailableNow === false) continue;
    if (!isWithinOperatingHours(cat.availabilityHours, now)) continue;
    const items: UiMenuItem[] = [];
    const categoryNameStr = localize(cat.name, lang) || cat.id;
    for (const it of cat.items ?? []) {
      if (!it) continue;
      items.push(
        toUiItem(it, cat.id, categoryNameStr, cat.name, lang, runningIdx++),
      );
    }
    if (items.length === 0) continue;
    categories.push({
      id: cat.id,
      label: localize(cat.name, lang) || cat.id,
      items,
    });
  }
  const allItems = categories.flatMap((c) => c.items);
  return { categories, items: allItems };
}

/* ──────── Modifier / Variant adapters ──────── */

export type UiVariantOption = {
  id: string;
  label: string;
  name: LocalizedString;
  price: number;
  isAvailable: boolean;
  imageUrl?: string | null;
  nutritionalInformation?: NutritionalInformationObj | null;
};

export type UiVariantGroup = {
  id: string;
  label: string;
  name: LocalizedString;
  options: UiVariantOption[];
};

export function mapModifierGroups(
  apiGroups: MenuRenderModifierGroup[] | undefined | null,
  lang: string,
  blockedModifierOptionIds?: ReadonlySet<string>,
): ModifierGroup[] {
  if (!apiGroups || apiGroups.length === 0) return [];
  const blocked = blockedModifierOptionIds ?? null;
  return apiGroups
    .filter(
      (g) => g && g.isAvailable !== false && g.options && g.options.length > 0,
    )
    .map((g) => {
      const min = typeof g.minSelection === "number" ? g.minSelection : 0;
      const max =
        typeof g.maxSelection === "number" && g.maxSelection > 0
          ? g.maxSelection
          : Infinity;
      const required = min > 0;
      const multi = g.maxSelection !== 1;
      return {
        id: g.id,
        label: localize(g.name, lang) || g.id,
        name: g.name,
        required,
        multi,
        min: multi ? min : undefined,
        max: multi ? (max === Infinity ? undefined : max) : undefined,
        restrictedToVariantOptionIds:
          g.restrictedToVariantOptionIds ?? undefined,
        options: (g.options ?? [])
          // Keep modifier options that are catalog-available; branch-blocked
          // ones stay visible but are flipped to isAvailable=false below so
          // the picker renders them disabled with a "Sold out" label rather
          // than hiding them.
          .filter((o) => o.isAvailable !== false)
          .map((o) => {
            const branchBlocked = !!blocked && blocked.has(o.id);
            return {
              id: o.id,
              label: localize(o.name, lang) || o.id,
              name: o.name,
              price:
                typeof o.price?.amount === "number"
                  ? o.price.amount
                  : Number(o.price?.amount ?? 0) || 0,
              imageUrl: o.imageUrl ?? null,
              nutritionalInformation: o.nutritionalInformation ?? null,
              defaultQuantity: o.defaultQuantity,
              maxQuantity: o.maxQuantity ?? null,
              isDefaultSelection: o.isDefaultSelection,
              isAvailable: !branchBlocked,
            };
          }),
      } as ModifierGroup;
    })
    .filter((g) => g.options.length > 0);
}

export function mapVariantGroups(
  apiGroups: MenuRenderVariantGroup[] | undefined | null,
  lang: string,
  blockedVariantOptionIds?: ReadonlySet<string>,
): UiVariantGroup[] {
  if (!apiGroups || apiGroups.length === 0) return [];
  const blocked = blockedVariantOptionIds ?? null;
  return apiGroups
    .filter(
      (g) => g && g.isAvailable !== false && g.options && g.options.length > 0,
    )
    .map((g) => ({
      id: g.id,
      label: localize(g.name, lang) || g.id,
      name: g.name,
      options: (g.options ?? [])
        // Same idea as modifier options: keep catalog-available variant
        // options visible; mark branch-blocked ones unavailable.
        .filter((o) => o.isAvailable !== false)
        .map((o) => ({
          id: o.id,
          label: localize(o.name, lang) || o.id,
          name: o.name,
          price: priceOf(o.price),
          isAvailable:
            o.isAvailable !== false && !(blocked && blocked.has(o.id)),
          imageUrl: (o as any).imageUrl ?? null,
          nutritionalInformation: o.nutritionalInformation ?? null,
        })),
    }))
    .filter((g) => g.options.length > 0);
}

/* ──────── Branch-level blocked items ──────── */

export type BranchBlocks = {
  /** Records with no variantOptionId — covers parent POS items AND modifier_options
   *  (their id spaces don't collide; the consumer disambiguates by which list
   *  the id appears in). */
  blockedItemIds: Set<string>;
  /** Variant_option ids blocked directly (record had a variantOptionId). */
  blockedVariants: Set<string>;
};

const EMPTY_BLOCKS: BranchBlocks = {
  blockedItemIds: new Set(),
  blockedVariants: new Set(),
};

/**
 * Polls `availabilityApi.check(branchId)` once on mount and every 60s
 * thereafter to discover items / variants / modifiers temporarily blocked
 * at the branch level. `useMenu` merges these into per-item, per-variant,
 * and per-modifier `isAvailable` flags.
 *
 * Mirrors v1's `home-page.service.ts` polling behaviour.
 */
export function useBranchBlocks(
  branchId: string | null | undefined,
): BranchBlocks {
  const [blocks, setBlocks] = useState<BranchBlocks>(EMPTY_BLOCKS);

  useEffect(() => {
    if (!branchId) {
      setBlocks(EMPTY_BLOCKS);
      return;
    }
    let cancelled = false;
    setBlocks(EMPTY_BLOCKS);
    const fetchOnce = async () => {
      try {
        const res = await availabilityApi.check(branchId);
        if (cancelled) return;
        const blockedItemIds = new Set<string>();
        const blockedVariants = new Set<string>();
        for (const b of res ?? []) {
          if (!b || !b.itemId) continue;
          const vo = (b as { variantOptionId?: string | null }).variantOptionId;
          if (vo) blockedVariants.add(vo);
          else blockedItemIds.add(b.itemId);
        }
        setBlocks({ blockedItemIds, blockedVariants });
      } catch {
        // Soft-fail: a transient availability fetch error shouldn't break
        // the menu. Keep whatever we had previously.
      }
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [branchId]);

  return blocks;
}

/** @deprecated Prefer `useBranchBlocks` — this returns only parent-item blocks. */
export function useBlockedItems(
  branchId: string | null | undefined,
): Set<string> {
  return useBranchBlocks(branchId).blockedItemIds;
}

/* ──────── Hook ──────── */

type CacheEntry = {
  promise: Promise<MenuRenderResponse>;
  data: MenuRenderResponse | null;
};

const menuCache = new Map<string, CacheEntry>();

export function clearMenuCache(): void {
  menuCache.clear();
}

export type UseMenuResult = {
  categories: UiCategory[];
  items: UiMenuItem[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
  /** Branch-level 86 blocks for the current branch. Consumers (e.g.
   *  ItemDetailScreen) pass these into mapVariantGroups / mapModifierGroups
   *  to mark variant_options and modifier_options sold-out. */
  branchBlocks: BranchBlocks;
  /** Id of the menu the items were rendered from (the published menu's own
   *  `menuId`, else the configured one); null when unknown. Sent on the
   *  price quote and the order so the server prices from the same menu. */
  menuId: string | null;
  /** The menu's tax policy is exempt: items carry no taxes. */
  menuTaxExempt: boolean;
};

export function useMenu(): UseMenuResult {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const {
    branch: activeBranch,
    branchId: ctxBranchId,
    branchSlug,
    loading: branchLoading,
  } = useBranch();

  // Derive sources, preferring branch context, falling back to env.
  // v1's MenuService loads from the branch `menuContentUrl`
  // (a Firebase Storage public JSON), NOT through the catalog gateway. We
  // mirror that ordering: prefer the URL, fall back to the catalog
  // /menus/{menuId}/render endpoint, then env.
  const cfgBranch = activeBranch ?? null;
  const menuContentUrl =
    (cfgBranch as { menuContentUrl?: string | null } | null)?.menuContentUrl ??
    null;
  const cfgMenuId =
    (cfgBranch as { menuId?: string | null } | null)?.menuId ?? null;
  const cfgBranchId = cfgBranch
    ? branchIdentifier(cfgBranch)
    : (ctxBranchId ?? null);
  const envMenuId =
    (import.meta.env.VITE_DEFAULT_MENU_ID as string | undefined) ?? null;
  const envBranchId =
    (import.meta.env.VITE_DEFAULT_BRANCH_ID as string | undefined) ?? null;
  const menuId = cfgMenuId ?? envMenuId;
  const branchId = cfgBranchId ?? envBranchId;

  const cacheKey =
    menuContentUrl ?? (menuId ? `${menuId}|${branchId ?? ""}` : null);
  const cached = cacheKey ? menuCache.get(cacheKey) : undefined;

  const [resp, setResp] = useState<MenuRenderResponse | null>(
    cached?.data ?? null,
  );
  const [loading, setLoading] = useState<boolean>(cached?.data ? false : true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [daypartTick, setDaypartTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (branchLoading) {
      setLoading(true);
      return () => {
        cancelled = true;
      };
    }
    if (!cacheKey) {
      setResp(null);
      setError(new Error("No menu source available for current branch"));
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const existing = menuCache.get(cacheKey);
    if (existing?.data && refreshTick === 0) {
      setResp(existing.data);
      setError(null);
      setLoading(false);
      const firstPrice = findFirstPriceCurrency(existing.data);
      if (firstPrice) setMenuCurrency(firstPrice, branchSlug);
      return () => {
        cancelled = true;
      };
    }

    setResp(existing?.data ?? null);
    setLoading(true);
    setError(null);

    const promise =
      existing?.promise && refreshTick === 0
        ? existing.promise
        : menuContentUrl
          ? menuApi.fetchFromUrl(menuContentUrl)
          : menuApi.getMenuRender(menuId!, {
              branchId: branchId ?? undefined,
            });
    menuCache.set(cacheKey, { promise, data: existing?.data ?? null });

    promise
      .then((data) => {
        menuCache.set(cacheKey, { promise, data });
        if (cancelled) return;
        setResp(data);
        const firstPrice = findFirstPriceCurrency(data);
        if (firstPrice) setMenuCurrency(firstPrice, branchSlug);
        setLoading(false);
      })
      .catch((err) => {
        menuCache.delete(cacheKey);
        if (cancelled) return;
        setResp(null);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    cacheKey,
    menuContentUrl,
    menuId,
    branchId,
    branchSlug,
    branchLoading,
    refreshTick,
  ]);

  useEffect(() => {
    if (!resp?.categories?.length) return;

    const delay = durationUntilNextMenuBoundary(
      resp.categories.map((category) => category.availabilityHours),
    );
    if (delay == null) return;

    const timer = window.setTimeout(() => {
      setDaypartTick((n) => n + 1);
    }, delay + 1000);

    return () => window.clearTimeout(timer);
  }, [resp, daypartTick]);

  const refresh = () => {
    if (cacheKey) menuCache.delete(cacheKey);
    setRefreshTick((n) => n + 1);
  };

  const branchBlocks = useBranchBlocks(branchId);

  const { categories, items } = useMemo(() => {
    const adapted = adaptMenuResponse(resp, lang);
    const blocked = branchBlocks.blockedItemIds;
    if (blocked.size === 0) return adapted;
    // Override `isAvailable` for any item present in the branch-level
    // blocked set. We rebuild the category items so consumers that close
    // over `categories` see the updated flag too.
    const applyBlock = (it: UiMenuItem): UiMenuItem =>
      blocked.has(it.id) && it.isAvailable ? { ...it, isAvailable: false } : it;
    const categories = adapted.categories.map((c) => ({
      ...c,
      items: c.items.map(applyBlock),
    }));
    const items = categories.flatMap((c) => c.items);
    return { categories, items };
  }, [resp, lang, branchBlocks, daypartTick]);

  const loadedMenuId =
    ((resp as { menuId?: string | null } | null)?.menuId ?? null) || menuId || null;
  const menuTaxExempt =
    String((resp as { taxPolicy?: unknown } | null)?.taxPolicy ?? "").toLowerCase() === "exempt";

  return { categories, items, loading, error, refresh, branchBlocks, menuId: loadedMenuId, menuTaxExempt };
}
